/*  MUI  */
import {
    Alert,
    AlertTitle,
    Box,
    Paper,
    TableCell,
    Typography
} from '@mui/material';

/*  Shared  */
import { PageTitle } from 'application/Shared/Components/App';
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { DateFormat } from 'application/Shared/Components/Core';
import { TimePeriod } from 'application/Shared/Components/Pickers';
import { ManagedTable } from 'application/Shared/Components/Table';
import { FontSizes } from 'application/Shared/Constants';
import { Response, Route, dateTimeString } from 'application/Shared/Helpers';
import { Insights } from 'application/Shared/Pages';
import { Requests } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/* Objects */
import { Application } from 'objects/Application';

/*  React  */
import React, { Component, useMemo } from 'react';
import { Trans, useTranslation, withTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { withRouter } from 'router/withRouter';

const RenderRow = ({ row }) => {
    const { t } = useTranslation();
    const { dateCreated, eventName, application, tenantAppExists } = row;

    return (
        <>
            <TableCell>
                <DateFormat date={dateCreated} />
            </TableCell>
            <TableCell>{eventName}</TableCell>
            <TableCell>
                {application && (
                    <Application
                        data={application}
                        fromPage={t('Common.insights_dashboard')}
                        nameHasLink={!!tenantAppExists}
                    >
                        <ApplicationNameLinkCell withCategory />
                    </Application>
                )}
            </TableCell>
        </>
    );
};

const InsightsTable = ({ parameters }) => {
    const { t } = useTranslation();

    const headers = useMemo(
        () => [
            {
                renderHeader: t('Common.timestamp'),
                sortable: true,
                sortableFieldType: 'date',
                sortableKey: 'dateCreated',
                width: '35%'
            },
            {
                renderHeader: t('Common.event'),
                sortable: true,
                sortableFieldType: 'string',
                sortableKey: 'eventName',
                width: '30%'
            },
            {
                renderHeader: t('Alert.affected_app'),
                sortable: true,
                sortableFieldType: 'string',
                sortableKey: 'application.name',
                width: '35%'
            }
        ],
        [t]
    );

    const searchFields = useMemo(
        () => [
            'application.baseName',
            'application.dateCreated',
            'application.name',
            'applicationId',
            'eventName',
            'id'
        ],
        []
    );

    return (
        <Paper elevation={0} variant="outlined" style={{ marginTop: '16px' }}>
            <ManagedTable
                headers={headers}
                parameters={parameters}
                renderBody={(row) => <RenderRow row={row} />}
                renderBodyKey="id"
                renderFooter
                rowsApi="/incident"
                rowsApiPreload
                rowsPerPage={10}
                searchEnabled
                searchFields={searchFields}
                sortDirection="DESC"
                sortFieldType="date"
                sortKey="dateCreated"
            />
        </Paper>
    );
};

class AdminInsights extends Component {
    constructor(props) {
        super(props);
        this.state = {
            dateSearch: { field: 'dateCreated', value: '' },
            end: new Date(),
            loaded: false,
            parameters: {},
            paramsLoaded: true,
            start: new Date(),
            total: 0,
            totalLoaded: false
        };

        /* requests */
        this.requests = new Requests();
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unload();
    }

    load = () => {
        this.setState({ loaded: true });
    };

    unload = () => {
        this.requests.cancel();
    };

    handleDate = (startDate, endDate) => {
        const start = dateTimeString(startDate);
        const end = dateTimeString(endDate);
        const value = `{"between": "${start}, ${end}"}`;

        // Don't make the api call if dates are invalid
        if (start === null || end === null) return;

        this.setState({
            dateSearch: { field: 'dateCreated', value },
            end,
            parameters: {
                dateCreated: value,
                excludeCommon: true,
                includeApp: true,
                includeGlobal: true,
                includeTenantAppExists: true
            },
            paramsLoaded: true,
            start
        });

        this.loadTotal(value);
    };

    loadTotal = async (activityTimestamp) => {
        const { match } = this.props;
        const { clientId } = match.params;
        debug.dev('Loading Total Insights', activityTimestamp);

        const params = {
            activityTimestamp,
            count: true,
            excludePersonal: true
        };
        if (clientId) {
            params.platformTenantId = clientId;
            params.includeClients = true;
        }
        try {
            const response = await axios.get('/appLog', {
                cancelToken: this.requests.token(),
                params
            });
            const total = Response.getCount(response);
            this.setState({ total, totalLoaded: true });
        } catch (e) {
            debug.e('Unable to load Total Insights', e);
        }
    };

    render() {
        const { match, t } = this.props;
        const {
            loaded,
            parameters,
            paramsLoaded,
            totalLoaded,
            total,
            dateSearch,
            start,
            end
        } = this.state;

        if (!loaded) {
            return null;
        }

        const { clientId, partnerId } = match.params;
        const baseAdminRoute = Route.buildBaseAdminRoute({
            clientId,
            partnerId
        });

        return (
            <>
                <Box display="flex" justifyContent="space-between">
                    <PageTitle title={t('Common.insights_dashboard')} />
                    <TimePeriod
                        period="month"
                        handleDate={(startDate, endDate) =>
                            this.handleDate(startDate, endDate)
                        }
                    />
                </Box>
                <Alert
                    severity="warning"
                    icon={false}
                    sx={{ marginBottom: '16px' }}
                >
                    <AlertTitle>
                        {t('Alerts.page_retirement_notice_title')}
                    </AlertTitle>
                    <Trans
                        i18nKey="Alerts.page_retirement_notice"
                        components={{
                            DashboardLink: (
                                <Link
                                    className="link underline"
                                    data-trackid="adminInsightsPageToBeRemovedAlertDashboardLink"
                                    to={baseAdminRoute}
                                >
                                    {t('Common.dashboard')}
                                </Link>
                            )
                        }}
                    />
                </Alert>
                {totalLoaded && (
                    <Typography
                        mb="16px"
                        fontSize={FontSizes[3]}
                        dangerouslySetInnerHTML={{
                            __html: t('Insights.discovered_across', {
                                totalNumber: total
                            })
                        }}
                    />
                )}
                <Insights dateSearch={dateSearch} start={start} end={end} />
                {paramsLoaded && <InsightsTable parameters={parameters} />}
            </>
        );
    }
}

export default withRouter(withTranslation()(AdminInsights));
